import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState } from "react";
// doc-design-sb2: standard bills workspace
import { BillTable } from "@/components/procurement/BillTable";
import { toast } from "sonner";
import { Plus, Loader2, Receipt, Pencil, Trash2, Upload, AlertTriangle, ShieldCheck, ShieldAlert, QrCode , Printer } from "lucide-react";
import { api } from "@/lib/api";
import { openPdf } from "@/lib/print";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

import { fmtDate } from "@/lib/format";
import { CompanyField } from "@/components/CompanyField";
import { confirmAction } from "@/lib/dialogs";

export default function ServiceBills() {
  const pf = useProjectFilter();
  const { can } = usePermissions();
  const canManage = can("BILLS", "create");
  const { companies } = useCompany() || {};
  const [bills, setBills] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [billRes, woRes, vendorRes] = await Promise.all([
        api.get("/procurement/service-bills"), api.get("/procurement/work-orders"), api.get("/procurement/vendors"),
      ]);
      setBills(billRes.data);
      setWorkOrders(woRes.data.filter((w) => w.status === "APPROVED"));
      setVendors(vendorRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const post = async (bill) => {
    setBusyId(bill.id);
    try {
      await api.post(`/procurement/service-bills/${bill.id}/post`);
      toast.success("Service bill posted to GL (Subcontract Cost)");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (bill) => {
    if (!await confirmAction(`Delete draft service bill ${bill.number}? Nothing has been posted, so nothing is reversed.`)) return;
    setBusyId(bill.id);
    try {
      await api.delete(`/procurement/service-bills/${bill.id}`);
      toast.success("Draft service bill deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <BillTable testId="service-bills-page" crumb="Service Bills" title="Service Bills" description="Bill a contractor against certified JMR quantities. Posting nets off retention & TDS." pf={pf} ProjectFilter={ProjectFilter}
      create={<CreateServiceBillDialog companies={companies} workOrders={workOrders} vendors={vendors} onCreated={load} />} bills={bills} vendors={vendors} loading={loading} totalKey="net_payable" totalLabel="Net Payable" canManage={canManage} canPost={can("BILLS", "post")} canDelete={can("BILLS", "delete")} busyId={busyId}
      pre="service-bill-row" postId="post-service-bill-button" printId="print-service-bills" deleteId="delete-service-bill-button" printBase="/print/service-bills" flowType="SERVICE_BILL" cancelBase="/procurement/service-bills"
      emptyTitle="No service bills yet" emptyId="service-bills-empty-state" reload={load} onPost={post} onRemove={remove} extraNote={undefined}
      renderEdit={(b) => <EditServiceBillDialog bill={b} workOrder={workOrders.find((w) => w.id === b.work_order_id)} onSaved={load} />} />
  );
}

function CreateServiceBillDialog({ companies, workOrders, vendors, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", work_order_id: "", vendor_bill_no: "", bill_date: "", retention_pct: "0", tds_pct: "0", tax_pct: "0" });
  const [qtys, setQtys] = useState({});
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [einvoice, setEinvoice] = useState(null);
  useResetOnClose(Boolean(open), () => { setForm({ company_id: "", work_order_id: "", vendor_bill_no: "", bill_date: "", retention_pct: "0", tds_pct: "0", tax_pct: "0" }); setQtys({}); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const selectedWO = workOrders.find((w) => w.id === form.work_order_id);

  const readInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setReading(true);
    setWarnings([]);
    setEinvoice(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/procurement/service-bills/extract", body, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({
        ...prev,
        company_id: data.company_id || prev.company_id,
        work_order_id: data.work_order_id || prev.work_order_id,
        vendor_bill_no: data.vendor_bill_no || prev.vendor_bill_no,
        bill_date: data.bill_date || prev.bill_date,
        retention_pct: data.retention_pct ?? prev.retention_pct,
        tds_pct: data.tds_pct ?? prev.tds_pct,
        tax_pct: data.tax_pct ?? prev.tax_pct,
      }));
      setQtys(Object.fromEntries((data.items || []).map((i) => [i.work_order_item_id, i.billed_qty])));
      setWarnings(data.warnings || []);
      setEinvoice(data.einvoice || null);
      if (data.work_order_id && !workOrders.some((w) => w.id === data.work_order_id)) {
        setWarnings((w) => [...w, "The matched work order is not in the list — refresh the page and try again."]);
      }
      toast.success(data.items?.length ? `Read ${data.items.length} line(s) from the invoice — please review` : "Invoice read — no lines could be matched");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReading(false);
    }
  };

  const selectWorkOrder = (id) => {
    const wo = workOrders.find((w) => w.id === id);
    const vendor = vendors?.find((v) => v.id === wo?.vendor_id);
    setForm({
      ...form,
      work_order_id: id,
      retention_pct: vendor ? String(vendor.default_retention_pct) : form.retention_pct,
      tds_pct: vendor ? String(vendor.default_tds_pct) : form.tds_pct,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.work_order_id) {
      toast.error("Pick a Work Order first");
      return;
    }
    setSaving(true);
    try {
      const items = Object.entries(qtys).filter(([, qty]) => qty).map(([work_order_item_id, billed_qty]) => ({ work_order_item_id, billed_qty }));
      await api.post("/procurement/service-bills", { ...form, items });
      toast.success("Service bill created");
      setForm({ company_id: "", work_order_id: "", vendor_bill_no: "", bill_date: "", retention_pct: "0", tds_pct: "0", tax_pct: "0" });
      setQtys({});
      setWarnings([]);
      setEinvoice(null);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-service-bill-button" data-tour="service-bill-new"><Plus className="h-4 w-4" /> New Service Bill</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Service Bill</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-service-bill-form">
          <div className="rounded-lg border border-dashed border-border p-3">
            <label className="flex cursor-pointer items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">Have the vendor's invoice? Upload it to fill this form.</span>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
                {reading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {reading ? "Reading…" : "Read invoice"}
              </span>
              <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" disabled={reading} onChange={readInvoice} data-testid="service-bill-invoice-upload" />
            </label>
            {einvoice && (
              <div
                className={`mt-2.5 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-xs ${
                  einvoice.signature === "verified"
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : einvoice.signature === "invalid"
                      ? "bg-rose-500/10 text-rose-700 dark:text-rose-400"
                      : "bg-sky-500/10 text-sky-700 dark:text-sky-400"
                }`}
                data-testid="service-bill-einvoice-badge"
              >
                {einvoice.signature === "verified" ? <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : einvoice.signature === "invalid" ? <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <QrCode className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                <span>
                  {einvoice.signature === "verified" ? "GST e-invoice verified" : einvoice.signature === "invalid" ? "E-invoice QR signature INVALID" : "GST e-invoice QR read (signature not checked)"}
                  {" · "}{einvoice.doc_no} · total ₹{Number(einvoice.total_value || 0).toLocaleString("en-IN")}
                  <span className="block font-mono text-[10px] opacity-75">IRN {String(einvoice.irn || "").slice(0, 24)}…</span>
                </span>
              </div>
            )}
            {warnings.length > 0 && (
              <ul className="mt-2.5 space-y-1 text-xs text-amber-700 dark:text-amber-400" data-testid="service-bill-invoice-warnings">
                {warnings.map((w, i) => (
                  <li key={i} className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="service-bill-company-select" />
            <div>
              <Label>Work order</Label>
              <Select required value={form.work_order_id} onValueChange={selectWorkOrder}>
                <SelectTrigger data-testid="service-bill-wo-select" className="mt-1.5"><SelectValue placeholder="Select an approved WO" /></SelectTrigger>
                <SelectContent>{workOrders.map((w) => <SelectItem key={w.id} value={w.id}>{w.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vendor bill no.</Label><Input data-testid="service-bill-vendor-bill-no-input" value={form.vendor_bill_no} onChange={set("vendor_bill_no")} className="mt-1.5" /></div>
            <div><Label>Bill date</Label><Input required type="date" data-testid="service-bill-date-input" value={form.bill_date} onChange={set("bill_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Retention % <span className="text-muted-foreground">(vendor default)</span></Label><Input type="number" step="0.01" data-testid="service-bill-retention-input" value={form.retention_pct} onChange={set("retention_pct")} className="mt-1.5" /></div>
            <div><Label>TDS % <span className="text-muted-foreground">(vendor default)</span></Label><Input type="number" step="0.01" data-testid="service-bill-tds-input" value={form.tds_pct} onChange={set("tds_pct")} className="mt-1.5" /></div>
            <div><Label>Tax %</Label><Input type="number" step="0.01" data-testid="service-bill-tax-input" value={form.tax_pct} onChange={set("tax_pct")} className="mt-1.5" /></div>
          </div>
          {selectedWO && (
            <div className="space-y-2" data-testid="service-bill-wo-items">
              <Label>Billed qty (certified vs. already billed shown for reference)</Label>
              {selectedWO.items.map((item) => (
                <div key={item.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 text-muted-foreground">{item.description} (certified {item.certified_qty}, billed {item.billed_qty})</span>
                  <Input type="number" step="0.001" className="w-28" value={qtys[item.id] || ""} onChange={(e) => setQtys({ ...qtys, [item.id]: e.target.value })} data-testid={`service-bill-item-qty-input-${item.id}`} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || !selectedWO} data-testid="create-service-bill-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditServiceBillDialog({ bill, workOrder, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({}); setQty({}); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (!open) return;
    const taxPct = Number(bill.subtotal) ? ((Number(bill.tax_amount) / Number(bill.subtotal)) * 100).toFixed(2) : "0";
    setForm({
      vendor_bill_no: bill.vendor_bill_no || "", bill_date: bill.bill_date,
      retention_pct: String(bill.retention_pct), tds_pct: String(bill.tds_pct), tax_pct: taxPct,
    });
    setQty(Object.fromEntries(bill.items.map((i) => [i.work_order_item_id, String(i.billed_qty)])));
  }, [open, bill]);

  const woLine = (id) => workOrder?.items?.find((i) => i.id === id);

  const save = async (e) => {
    e.preventDefault();
    const items = Object.entries(qty)
      .filter(([, q]) => Number(q) > 0)
      .map(([work_order_item_id, billed_qty]) => ({ work_order_item_id, billed_qty }));
    if (!items.length) {
      toast.error("Keep at least one line with a quantity — or delete the draft instead");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/procurement/service-bills/${bill.id}`, { ...form, vendor_bill_no: form.vendor_bill_no || null, items });
      toast.success("Service bill updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" title="Edit draft" data-testid={`edit-service-bill-button-${bill.number}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Edit {bill.number}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vendor bill no.</Label><Input value={form.vendor_bill_no || ""} onChange={set("vendor_bill_no")} className="mt-1.5" data-testid="edit-service-bill-vendor-bill-no" /></div>
            <div><Label>Bill date</Label><Input required type="date" value={form.bill_date || ""} onChange={set("bill_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Retention %</Label><Input type="number" step="0.01" min="0" max="100" value={form.retention_pct ?? ""} onChange={set("retention_pct")} className="mt-1.5" /></div>
            <div><Label>TDS %</Label><Input type="number" step="0.01" min="0" max="100" value={form.tds_pct ?? ""} onChange={set("tds_pct")} className="mt-1.5" /></div>
            <div><Label>Tax %</Label><Input type="number" step="0.01" min="0" max="100" value={form.tax_pct ?? ""} onChange={set("tax_pct")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Lines</Label>
            <div className="mt-1.5 space-y-2">
              {bill.items.map((item) => {
                const line = woLine(item.work_order_item_id);
                return (
                  <div key={item.id} className="flex items-center gap-2">
                    <div className="min-w-0 flex-1 text-sm">
                      <div className="truncate text-foreground">{line?.description || "Work order line"}</div>
                      {line && <div className="text-[11px] text-muted-foreground">Certified {line.certified_qty} · already billed {line.billed_qty} · rate {item.rate}</div>}
                    </div>
                    <Input type="number" step="0.001" min="0" className="w-28" value={qty[item.work_order_item_id] ?? ""}
                      onChange={(e) => setQty({ ...qty, [item.work_order_item_id]: e.target.value })} />
                  </div>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Set a line to 0 to remove it from this bill.</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentBadge({ due, paid }) {
  if (due <= 0) return null;
  const balance = due - paid;
  const [label, tone] =
    balance <= 0.005
      ? ["Paid", "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"]
      : paid > 0
        ? ["Partly paid", "bg-amber-500/10 text-amber-600 dark:text-amber-400"]
        : ["Unpaid", "bg-rose-500/10 text-rose-600 dark:text-rose-400"];
  return <span className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>;
}

const inrAmount = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
